import { StateSlice } from '@/stores/appStore';
import { getCurrentTime, snakeToCamel } from '@/lib/utils';
import { toast } from 'react-hot-toast';
import { ID, PaperId } from '@/stores/slices/paperSlice';
import { WrapResponse } from '@/types';
import { fun } from '@/lib/request';
import { api } from '@/lib/env';

export type CreateTranslateParams = {
  paperId: PaperId;
  startPage: number;
  endPage: number;
  couponId: number;
}
export type TranslateListItem = {
  id: ID;
  paperId: PaperId;
  startPage: number;
  endPage: number;
  status: number; // 0,1,2,-1的规则
  createTime: string;
}

export type TranslateContentListItem = {
  type: 'text';
  text: string;
  textLevel: number;
  pageIdx: number;
  textTrans: string;
} | {
  type: 'equation';
  text: string;
  textFormat: 'latex';
  pageIdx: number;
} | {
  type: 'image';
  imgPath: string;
  imgCaption: string[];
  imgFootnote: string[];
  pageIdx: number;
  imgCaptionTrans: string[];
  imgFootnoteTrans: string[];
} | {
  type: 'table';
  imgPath: string;
  tableCaption: string[];
  tableFootnote: string[];
  tableBody: string; // html
  pageIdx: number;
  tableCaptionTrans: string[];
  tableFootnoteTrans: string[];
  tableBodyTrans: string;
}


export type FetchTranslateListParams = {
  paperId: PaperId;
}
export type FetchTranslateDetailParams = {
  id: ID;
}
type FetchTranslateListData = {
  list: TranslateListItem[];
}
type FetchTranslateDetailData = {
  detail: TranslateDetailItem
}

export type TranslateDetailItem = TranslateListItem & {
  contentList: TranslateContentListItem[];
}

export type TranslateListMap = Record<PaperId, TranslateListItem[]>;
export type TranslateDetailMap = Record<ID, TranslateDetailItem>;
export type ExportFileType = 'word' | 'markdown';
// org/trans/both，原文/译文/双语
export type ExportType = 'org' | 'trans' | 'both';
export type ExportTranslatedResultParams = {
  taskId: string;
  isTrans: boolean;
  fileType: ExportFileType;
  exportType: ExportType;
}
const namespace = 'transition';
export type TranslateTextParams = {
  input: string;
}
export type TranslateTextData = {
  output: string;
}

export type TranslationSlice = {
  [namespace]: {
    translateListMap: TranslateListMap;
    translateDetailMap: TranslateDetailMap;
    createTranslate: (params: CreateTranslateParams) => Promise<void>;
    fetchTranslateList: (params: FetchTranslateListParams) => Promise<TranslateListItem[] | null>;
    fetchTranslateDetail: (params: FetchTranslateDetailParams) => Promise<TranslateDetailItem | null>;
    fetchTranslateDetailDebounced: (params: FetchTranslateDetailParams, wait?: number) => Promise<TranslateDetailItem | null>;
    exportTranslatedResult: (params: ExportTranslatedResultParams) => Promise<{
      filename: string;
      data: any;
    } | null>;
    translateText: (params: TranslateTextParams) => Promise<string | null>;
  }
};

export const createTranslationSlice: StateSlice<TranslationSlice> = (set, _get) => {
  const get = () => _get()[namespace];
  return {
    [namespace]: {
      translateListMap: {} as TranslateListMap,
      translateDetailMap: {} as TranslateDetailMap,
      createTranslate: async (params) => {
        try {
          const {
            code,
            message,
          } = (await fun.post<WrapResponse<{}>>(
            api('/paper/translate/create', true),
            {
              'paper_id': params.paperId,
              'start_page': params.startPage,
              'end_page': params.endPage,
              'coupon_id': params.couponId?.toString(),
            }
          )).data;
          if (code === 0) {
            // OK
            const refreshUserInfo = _get().refreshUserInfo;

            refreshUserInfo();
          } else if (message) {
            toast.error(message);
          }
        } catch (err) {
          console.error(err);
        }
      },
      fetchTranslateList: async ({ paperId }) => {
        try {
          const {
            code,
            data,
            message,
          } = (await fun.post<WrapResponse<FetchTranslateListData>>(
            api('/paper/translate/list', true),
            {
              paper_id: paperId,
            }
          )).data;
          if (code === 0) {
            const list = snakeToCamel<FetchTranslateListData>(data).list;
            set(({ [namespace]: state }) => {
              state.translateListMap[paperId] = list.sort((item1, item2) => {
                const time1 = new Date(item1.createTime).getTime();
                const time2 = new Date(item2.createTime).getTime();
                return time2 - time1;
              });
            });
          } else {
            toast.error(message);
          }
        } catch (err) {
          console.log(err);
        } finally {
          return get().translateListMap[paperId] || null;
        }
      },
      fetchTranslateDetail: async ({ id }) => {
        try {
          const {
            code,
            data,
            message,
          } = (await fun.post<WrapResponse<FetchTranslateDetailData>>(
            api('/paper/translate/detail', true),
            {
              id,
            }
          )).data;
          if (code === 0) {
            const detail = snakeToCamel<FetchTranslateDetailData>(data).detail;
            set(({ [namespace]: state }) => {
              state.translateDetailMap[id] = detail;
            });
          } else {
            toast.error(message);
          }
        } catch (err) {
        } finally {
          return get().translateDetailMap[id] || null;
        }
      },
      fetchTranslateDetailDebounced: (() => {
        // 用于存储每个 id 的最新 resolve
        let timer: ReturnType<typeof setTimeout> | null = null;
        let lastArgs: FetchTranslateDetailParams | null = null;
        let lastPromise: Promise<TranslateDetailItem | null> | null = null;
        let lastResolve: ((value: TranslateDetailItem | null) => void) | null = null;
        let lastWait = 200;
        return (params: FetchTranslateDetailParams, wait?: number) => {
          lastArgs = params;
          if (timer) clearTimeout(timer);
          if (lastPromise && lastResolve) {
            // 上一个 promise 被覆盖，直接 resolve null（或不处理）
            lastResolve(null);
          }
          lastPromise = new Promise<TranslateDetailItem | null>((resolve) => {
            lastResolve = resolve;
            timer = setTimeout(async () => {
              if (!lastArgs) return resolve(null);
              const result = await get().fetchTranslateDetail(lastArgs);
              resolve(result);
              lastArgs = null;
              lastPromise = null;
              lastResolve = null;
            }, wait ?? lastWait);
          });
          return lastPromise;
        };
      })(),
      exportTranslatedResult: async ({
        taskId,
        fileType,
        isTrans,
        exportType,
      }) => {
        try {
          const response = (
            await fun.post(
              api('/paper/translate/export', true),
              {
                task_id: taskId,
                file_type: fileType,
                is_trans: isTrans,
                export_type: exportType,
              },
              {
                responseType: 'blob',
              },
            )
          );
          // 获取文件名
          let filename = `Findin文献翻译_${getCurrentTime().replace(/[\s\-]+/g, '_')}.${fileType === 'word' ? 'docx' : 'md'}`;
          const disposition = response.headers['content-disposition']
          if (disposition) {
            // 优先匹配 filename*=UTF-8''xxx
            let match = disposition.match(/filename\*\=UTF-8''([^;]+)/i);
            if (match) {
              filename = decodeURIComponent(match[1]);
              console.log('filename', filename)
            }
          }
          return {
            filename,
            data: response.data,
          }
        } catch (error) {
          console.error('Error export translated result:', error);
          return null;
        }
      },
      translateText: async ({ input }) => {
        try {
          const {
            code,
            data,
            message,
          } = (await fun.post<WrapResponse<TranslateTextData>>(
            api('/common/translate', true),
            {
              input,
            }
          )).data;
          if (code === 0) {
            return snakeToCamel<TranslateTextData>(data).output;
          } else {
            toast.error(message || '翻译失败');
            return null;
          }
        } catch (error) {
          console.error('Error translate text:', error);
          toast.error('翻译失败，请稍后重试');
          return null;
        }
      },
    },
  };
}


/**
 * 将 pageIdx 直接映射到数组索引，空缺处填 null
 * @param contentList 原始数据
 * @returns 数组索引对应 pageIdx 的二维数组（可能存在 null）
 */
export const mapPageIdxToArray = (
  contentList: TranslateContentListItem[]
): (TranslateContentListItem[] | null)[] => {
  // 1. 找出最大 pageIdx 以确定数组长度
  const maxPageIdx = contentList.reduce(
    (max, item) => Math.max(max, item.pageIdx),
    0
  );

  // 2. 初始化结果数组（长度 maxPageIdx + 1，全部填充 null）
  const result: (TranslateContentListItem[] | null)[] = new Array(maxPageIdx + 1)
    .fill(null);

  // 3. 按 pageIdx 分组
  contentList.forEach((item) => {
    const { pageIdx } = item;
    if (!result[pageIdx]) {
      result[pageIdx] = [];
    }
    result[pageIdx]!.push(item); // 非 null 断言（因前一行已初始化）
  });

  return result;
};